import { computed, inject, reactive, readonly } from 'vue';

export const Ccm19Embedding = Object.freeze({
    YouTube: 'YouTube Video',
    TradingView: 'TradingView',
    GoogleAnalytics4: 'Google Analytics 4',
    Snowplow: 'Snowplow',
    GoogleTagManager: 'Google Tag Manager',
    LiveChat: 'LiveChat',
    GoogleAdsConversion: 'Google Ads Conversion Tracking',
    GoogleAdsRemarketing: 'Google Ads Remarketing',
    MetaPixel: 'Meta Pixel',
    Facebook: 'Facebook',
    Twitter: 'Twitter',
    MicrosoftConversion: 'Microsoft Conversion Tracking',
    ShopifyPixel: 'Shopify Pixel',
    Ccm19: 'Cookie Consent Manager CCM19'
});

/* ==========================================================================
   INTERNAL HELPERS
   ========================================================================== */

const CCM19_KEY = Symbol('CCM19_GLOBAL');

function getCCM() {
    if (typeof window === 'undefined') return null;

    return window.CCM || null;
}

function readAcceptedEmbeddings() {
    const CCM = getCCM();
    const set = new Set();

    const list = CCM?.acceptedEmbeddings;

    if (!Array.isArray(list)) return set;

    for (const item of list) {
        // Case 1: strings (your current setup)
        if (typeof item === 'string') {
            set.add(item);
            continue;
        }

        // Case 2: objects { name, code, ... }
        if (item && typeof item === 'object') {
            if (item.code) set.add(item.code);

            if (item.name) set.add(item.name);
        }
    }

    return set;
}

/* ==========================================================================
   STORE FACTORY (SINGLETON)
   ========================================================================== */

function createCcm19Store() {
    const state = reactive({
        isReady: false,
        fullConsentGiven: false,
        accepted: new Set()
    });

    const refresh = () => {
        const CCM = getCCM();

        state.accepted = readAcceptedEmbeddings();
        state.fullConsentGiven = !!CCM?.fullConsentGiven;
    };

    const markReady = () => {
        state.isReady = true;
        refresh();
    };

    const onReady = (cb) => {
        const safeCb = typeof cb === 'function' ? cb : () => {};
        const CCM = getCCM();

        if (CCM) {
            markReady();
            safeCb();

            return;
        }

        window.addEventListener(
            'ccm19WidgetLoaded',
            () => {
                markReady();
                safeCb();
            },
            { once: true }
        );
    };

    const openSettings = () => {
        const CCM = getCCM();

        if (!CCM) return;

        if (typeof CCM.openControlPanel === 'function') CCM.openControlPanel();
        else if (typeof CCM.openWidget === 'function') CCM.openWidget();
    };

    /* ----------------------------------------------------------------------
       GLOBAL EVENT LISTENERS
       ---------------------------------------------------------------------- */

    if (typeof window !== 'undefined') {
        window.addEventListener('ccm19WidgetLoaded', markReady);
        window.addEventListener('ccm19EmbeddingAccepted', refresh);
        window.addEventListener('ccm19WidgetClosed', refresh);
    }

    /* ----------------------------------------------------------------------
       CONVENIENCE COMPUTEDS
       ---------------------------------------------------------------------- */

    const fullConsentGiven = computed(() => state.fullConsentGiven);
    const youtube = computed(() => state.accepted.has(Ccm19Embedding.YouTube));
    const facebook = computed(() => state.accepted.has(Ccm19Embedding.Facebook));
    const twitter = computed(() => state.accepted.has(Ccm19Embedding.Twitter));
    const tradingView = computed(() => state.accepted.has(Ccm19Embedding.TradingView));
    const liveChat = computed(() => state.accepted.has(Ccm19Embedding.LiveChat));

    const has = (codeOrName) => computed(() => state.fullConsentGiven || state.accepted.has(codeOrName));

    /* ---------------------------------------------------------------------- */

    return {
        state: readonly(state),
        onReady,
        refresh,
        openSettings,
        has,
        youtube,
        facebook,
        twitter,
        tradingView,
        liveChat,
        fullConsentGiven
    };
}

let _store = null;

function getStore() {
    if (!_store) _store = createCcm19Store();

    return _store;
}

/* ==========================================================================
   PLUGIN
   ========================================================================== */

export const Ccm19Plugin = {
    install(app) {
        app.provide(CCM19_KEY, getStore());
    }
};

/* ==========================================================================
   COMPOSABLE
   ========================================================================== */

export function useCcm19Global() {
    const injected = inject(CCM19_KEY, null);

    return injected || getStore();
}
